import apiClient, { type ApiError } from './apiClient'
import type {
  PaymentConfig,
  PaymentRequest,
  PaymentResponse,
} from '@/types/payment'
import { API_ENDPOINTS } from './config'
import { isAuthError, createAuthError, extractErrorMessage } from '@/lib/utils/errorHandling'

/**
 * Get payment configuration from API
 * @returns Payment configuration data
 * @throws ApiError if request fails
 */
export async function getPaymentConfig(): Promise<PaymentConfig> {
  try {
    const response = await apiClient.get<PaymentConfig>(API_ENDPOINTS.PAYMENT_CONFIG, {
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Accept-Language': 'en-GB,en-US;q=0.9,en;q=0.8',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
    })

    return response.data || {}
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to fetch payment configuration.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Process CardConnect payment
 * @param paymentData - Payment request data
 * @returns Payment response
 * @throws ApiError if request fails
 */
export async function processCardConnectPayment(
  paymentData: PaymentRequest
): Promise<PaymentResponse> {
  try {
    const response = await apiClient.post<PaymentResponse>(
      API_ENDPOINTS.PAYMENT_CARDCONNECT,
      paymentData,
      {
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
      }
    )

    const responseData = response.data

    // Handle response structure
    if (responseData && typeof responseData === 'object') {
      const paymentResponse = responseData as PaymentResponse

      // If API returns success: false, throw an error with the message
      if (paymentResponse.success === false) {
        throw {
          message: paymentResponse.message || 'Payment processing failed. Please try again.',
          status: 400,
        } as ApiError
      }

      return paymentResponse
    }

    return {
      success: true,
      data: responseData as unknown as PaymentResponse['data'],
    }
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }

    // Extract detailed error message from API response
    let errorMessage = 'Payment processing failed. Please try again.'

    if (error && typeof error === 'object') {
      const apiError = error as { response?: { data?: unknown }; message?: string }

      // Check for error in response.data
      if (apiError.response?.data) {
        const responseData = apiError.response.data

        // Handle string error message
        if (typeof responseData === 'string') {
          errorMessage = responseData
        }
        // Handle object with message property
        else if (responseData && typeof responseData === 'object') {
          const rd = responseData as {
            message?: string
            error?: string
            error_message?: string
            msg?: string
            errors?: Record<string, unknown>
          }
          errorMessage = rd.message ||
            rd.error ||
            rd.error_message ||
            rd.msg ||
            errorMessage

          // Handle validation errors
          if (rd.errors && typeof rd.errors === 'object') {
            const errorMessages = Object.values(rd.errors).flat()
            if (errorMessages.length > 0) {
              errorMessage = Array.isArray(errorMessages[0])
                ? errorMessages[0].join(', ')
                : String(errorMessages[0])
            }
          }
        }
      }
      // Check for error.message directly
      else if (apiError.message) {
        errorMessage = apiError.message
      }
    }

    throw {
      message: errorMessage,
      status: (error as ApiError).status || 400,
    } as ApiError
  }
}

/**
 * Process PayPal payment
 * @param applicantId - Applicant ID
 * @param paymentData - Payment request data
 * @returns Payment response
 * @throws ApiError if request fails
 */
export async function processPayPalPayment(
  applicantId: string | number,
  paymentData: Partial<PaymentRequest>
): Promise<PaymentResponse> {
  try {
    const response = await apiClient.post<PaymentResponse>(
      API_ENDPOINTS.PAYMENT_PAYPAL(applicantId),
      paymentData,
      {
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
      }
    )

    const responseData = response.data

    // Handle response structure
    if (responseData && typeof responseData === 'object') {
      const paymentResponse = responseData as PaymentResponse

      // If API returns success: false, throw an error with the message
      if (paymentResponse.success === false) {
        throw {
          message: paymentResponse.message || 'PayPal payment processing failed. Please try again.',
          status: 400,
        } as ApiError
      }

      return paymentResponse
    }

    return {
      success: true,
      data: responseData as unknown as PaymentResponse['data'],
    }
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }

    // Extract detailed error message from API response
    let errorMessage = 'PayPal payment processing failed. Please try again.'

    if (error && typeof error === 'object') {
      const apiError = error as { response?: { data?: unknown }; message?: string }

      // Check for error in response.data
      if (apiError.response?.data) {
        const responseData = apiError.response.data

        // Handle string error message
        if (typeof responseData === 'string') {
          errorMessage = responseData
        }
        // Handle object with message property
        else if (responseData && typeof responseData === 'object') {
          const rd = responseData as {
            message?: string
            error?: string
            error_message?: string
            msg?: string
            errors?: Record<string, unknown>
          }
          errorMessage = rd.message ||
            rd.error ||
            rd.error_message ||
            rd.msg ||
            errorMessage

          // Handle validation errors
          if (rd.errors && typeof rd.errors === 'object') {
            const errorMessages = Object.values(rd.errors).flat()
            if (errorMessages.length > 0) {
              errorMessage = Array.isArray(errorMessages[0])
                ? errorMessages[0].join(', ')
                : String(errorMessages[0])
            }
          }
        }
      }
      // Check for error.message directly
      else if (apiError.message) {
        errorMessage = apiError.message
      }
    }

    throw {
      message: errorMessage,
      status: (error as ApiError).status || 400,
    } as ApiError
  }
}

/**
 * Check last payment attempt status (GET - legacy)
 * @param applicationId - Application ID
 * @returns Payment attempt status
 */
export async function checkLastPaymentAttempt(applicationId: string | number): Promise<{
  allowPayment: boolean
  retryPaymentAfter?: number
  paymentWindow?: number
  [key: string]: unknown
}> {
  try {
    const response = await apiClient.get(API_ENDPOINTS.PAYMENT_CHECK_LAST_ATTEMPT(applicationId))
    return response.data as {
      allowPayment: boolean
      retryPaymentAfter?: number
      paymentWindow?: number
      [key: string]: unknown
    }
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to check payment status.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Initialize payment (POST) - canonical API to prevent double payment and get payment window.
 * Same contract as Angular: validates whether the applicant can start a payment, returns window and balanceDue.
 * @param applicationId - Application ID (number)
 * @param applicantId - Applicant ID (number)
 * @param paymentType - 'APPLICATION_FEE' | 'CONTRACT' | 'DEFAULT_INVOICE'
 * @returns allowPayment, balanceDue, paymentWindow, retryPaymentAfter
 */
export interface InitializePaymentResponse {
  allowPayment: boolean
  balanceDue?: number
  paymentWindow?: number
  retryPaymentAfter?: number
  [key: string]: unknown
}

export async function initializePayment(
  applicationId: number,
  applicantId: number,
  paymentType: 'APPLICATION_FEE' | 'CONTRACT' | 'DEFAULT_INVOICE'
): Promise<InitializePaymentResponse> {
  try {
    const response = await apiClient.post<InitializePaymentResponse>(
      API_ENDPOINTS.PAYMENT_INITIALIZE,
      { applicationId, applicantId, paymentType },
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    )
    const data = response.data as InitializePaymentResponse
    return {
      ...data,
      allowPayment: !!data.allowPayment,
      balanceDue: data.balanceDue,
      paymentWindow: data.paymentWindow != null ? Number(data.paymentWindow) : undefined,
      retryPaymentAfter: data.retryPaymentAfter != null ? Number(data.retryPaymentAfter) : undefined,
    }
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to initialize payment.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Get applicant rent amount
 * @param applicantId - Applicant ID
 * @returns Rent amount
 * @throws ApiError if request fails
 */
export async function getApplicantRentAmount(
  applicantId: string | number
): Promise<number> {
  try {
    const response = await apiClient.get<string | number>(
      API_ENDPOINTS.APPLICANT_RENT_AMOUNT(applicantId)
    )

    // Handle both string and number responses
    if (typeof response.data === 'number') {
      return response.data
    }

    if (typeof response.data === 'string') {
      const parsed = parseFloat(response.data)
      return isNaN(parsed) ? 0 : parsed
    }

    return 0
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to fetch rent amount.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Confirm application fee payment after successful cardconnect payment
 * @param applicantId - Applicant ID
 * @param paymentSource - Payment source (e.g., 'CARDCONNECT')
 * @param authCode - Authorization code from payment response
 * @param tabid - Tab ID from session
 * @returns Payment confirmation response
 * @throws ApiError if request fails
 */
export async function confirmApplicationFeePayment(
  applicantId: string | number,
  paymentSource: string,
  authCode: string,
  tabid?: string
): Promise<PaymentResponse> {
  try {
    const response = await apiClient.post<PaymentResponse>(
      API_ENDPOINTS.PAYMENT_PAYPAL(applicantId), // This endpoint is used for application_fee confirmation
      {
        payment_source: paymentSource,
        payment_data: {
          auth_code: authCode,
        },
        ...(tabid && { tabid }),
      },
      {
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
      }
    )

    const responseData = response.data

    // Handle response structure
    if (responseData && typeof responseData === 'object') {
      return responseData as PaymentResponse
    }

    return {
      success: true,
      data: responseData as unknown as PaymentResponse['data'],
    }
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to confirm application fee payment.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Confirm rent/contract payment after successful payment processing
 * @param applicantId - Applicant ID
 * @param paymentSource - Payment source (e.g., 'CARDCONNECT', 'PAYPAL')
 * @param authCode - Authorization code from payment response
 * @param paymentAmount - Payment amount (for partial payments)
 * @param tabid - Tab ID from session
 * @returns Payment confirmation response
 * @throws ApiError if request fails
 */
export async function confirmRentPayment(
  applicantId: string | number,
  paymentSource: string,
  authCode: string,
  paymentAmount?: string | number,
  tabid?: string
): Promise<PaymentResponse> {
  try {
    const requestData: Record<string, unknown> = {
      payment_source: paymentSource,
      payment_data: {
        auth_code: authCode,
        ...(paymentAmount && { payment_amount: String(paymentAmount) }),
      },
      ...(tabid && { tabid }),
    }

    const response = await apiClient.post<PaymentResponse>(
      API_ENDPOINTS.PAYMENT_RENT(applicantId),
      requestData,
      {
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
      }
    )

    const responseData = response.data

    // Handle response structure
    if (responseData && typeof responseData === 'object') {
      return responseData as PaymentResponse
    }

    return {
      success: true,
      data: responseData as unknown as PaymentResponse['data'],
    }
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to confirm rent payment.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Confirm default invoice payment after successful payment processing
 * @param applicantId - Applicant ID
 * @param paymentSource - Payment source (e.g., 'CARDCONNECT', 'PAYPAL')
 * @param authCode - Authorization code from payment response
 * @param paymentAmount - Payment amount
 * @param tabid - Tab ID from session
 * @returns Payment confirmation response
 * @throws ApiError if request fails
 */
export async function confirmDefaultPayment(
  applicantId: string | number,
  paymentSource: string,
  authCode: string,
  paymentAmount?: string | number,
  tabid?: string
): Promise<PaymentResponse> {
  try {
    const requestData: Record<string, unknown> = {
      payment_source: paymentSource,
      payment_data: {
        auth_code: authCode,
        ...(paymentAmount && { payment_amount: String(paymentAmount) }),
      },
      ...(tabid && { tabid }),
    }

    const response = await apiClient.post<PaymentResponse>(
      API_ENDPOINTS.PAYMENT_DEFAULT(applicantId),
      requestData,
      {
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
      }
    )

    const responseData = response.data

    // Handle response structure
    if (responseData && typeof responseData === 'object') {
      return responseData as PaymentResponse
    }

    return {
      success: true,
      data: responseData as unknown as PaymentResponse['data'],
    }
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }

    throw {
      message: extractErrorMessage(error, 'Failed to confirm default invoice payment.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}


/**
 * Refund Payment
 * @param paymentLogId - Payment Log ID
 * @param refundData - Refund details (amount, notes)
 * @throws ApiError if request fails
 */
export async function refundPayment(
  paymentLogId: string | number,
  refundData: { amount: number; notes?: string }
): Promise<void> {
  try {
    await apiClient.post(`/api/refund_payment`, {
      payment_log_id: paymentLogId,
      refund_amount: refundData.amount,
      notes: refundData.notes,
    })
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to refund payment.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Delete Payment Log
 * @param paymentLogId - Payment Log ID
 * @throws ApiError if request fails
 */
export async function deletePayment(paymentLogId: string | number): Promise<void> {
  try {
    await apiClient.delete(`/api/payment_log/${paymentLogId}`)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to delete payment log.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/** Angular `deletePayment`: adjustment rows use `pa_id`, otherwise `payment_log_id`. */
export async function deletePaymentLogOrAdjustment(payment: {
  payment_log_id?: string | number
  pa_id?: string | number
}): Promise<void> {
  const paId = payment.pa_id
  const logId = payment.payment_log_id
  const path =
    paId != null && paId !== ''
      ? `/api/payment_adjustment/${paId}`
      : logId != null && logId !== ''
        ? `/api/payment_log/${logId}`
        : null
  if (!path) {
    throw { message: 'Missing payment log or adjustment id.' } as ApiError
  }
  try {
    await apiClient.delete(path)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to delete payment log.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}
